// GenToken/ListTokens.js
import React, { useState } from 'react';
import axios from 'axios';
import './ListTokens.css';
import { truncateAddress } from './tokenUtils';
import { displayNetworkName } from './solanaConfig';
import RegisterMetadata from './RegisterMetadata';

const ListTokens = ({ transactions, language, network, fetchTransactions }) => {
  const [selectedToken, setSelectedToken] = useState(null);
  const [showRegisterWindow, setShowRegisterWindow] = useState(false);
  // 삭제 확인 오버레이를 위한 state 추가
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';

  // 메타데이터 등록 버튼 클릭 핸들러
  const handleRegisterClick = (token) => {
    setSelectedToken(token);
    setShowRegisterWindow(true);
  };

  // 메타데이터 등록 창 닫기 핸들러
  const handleCloseRegister = () => {
    setShowRegisterWindow(false);
    setSelectedToken(null);
  };

  // 메타데이터 등록 성공 핸들러
  const handleRegisterSuccess = () => {
    // 필요한 경우 부모 컴포넌트에 알림
    setTimeout(() => {
      setShowRegisterWindow(false);
      setSelectedToken(null);
      // 페이지 새로고침 또는 목록 업데이트 로직 추가 가능
    }, 2000);
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = (token) => {
    setTokenToDelete(token);
    setShowDeleteConfirm(true);
    setDeleteError(null);
  };

  // 삭제 확인 취소 핸들러
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTokenToDelete(null);
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = async () => {
    if (!tokenToDelete) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      // 서버에 삭제 요청
      const response = await axios.delete(`${SERVER_ROOT}/api/solana/token/delete`, {
        params: {
          tokenAddress: tokenToDelete.tokenAddress,
          userId: tokenToDelete.userId,
        }
      });
      
      // 성공적으로 삭제되면 모달 닫기 및 목록 새로고침
      setShowDeleteConfirm(false);
      setTokenToDelete(null);
      
      // fetchTransactions 함수가 제공된 경우 호출
      if (typeof fetchTransactions === 'function') {
        fetchTransactions();
      }
      
    } catch (error) {
      console.error('Error deleting token:', error);
      setDeleteError(
        language === 'KR'
          ? '토큰 삭제 중 오류가 발생했습니다. 다시 시도해주세요.'
          : 'An error occurred while deleting the token. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <div className="no-tokens-message">
        {language === 'KR' 
          ? '아직 생성된 토큰이 없습니다.' 
          : 'No tokens created yet.'}
      </div>
    );
  }

  return (
    <div className="transactions-history">
      <h3 className="step-title">
        {language === 'KR' ? '내 토큰 목록' : 'My Tokens'}
      </h3>
      <p className="step-description">
        {language === 'KR' 
          ? '귀하가 생성한 모든 토큰 목록입니다. 상세 정보 확인 및 메타데이터를 등록할 수 있습니다.' 
          : 'List of all tokens you have created. You can view details and register metadata.'}
      </p>
      
      <table className="token-list-table">
        <thead>
          <tr>
            <th>{language === 'KR' ? '토큰 이름' : 'Token Name'}</th>
            <th>{language === 'KR' ? '토큰 주소' : 'Token Address'}</th>
            <th>{language === 'KR' ? '네트워크' : 'Network'}</th>
            <th>{language === 'KR' ? '초기 발행량' : 'Initial Supply'}</th>
            <th>{language === 'KR' ? '생성일' : 'Created'}</th>
            <th>{language === 'KR' ? '메타데이터' : 'Metadata'}</th>
            <th>{language === 'KR' ? '작업' : 'Actions'}</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx, index) => (
            <tr key={index} className="token-item">
              <td className="token-name-symbol">
                <strong>{tx.token_name}</strong> ({tx.token_symbol})
              </td>
              <td className="token-address">
                <a 
                  href={`https://explorer.solana.com/address/${tx.token_address}?cluster=${tx.network}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="explorer-link"
                >
                  {truncateAddress(tx.token_address, 6, 6)}
                </a>
              </td>
              <td className="token-network">
                {displayNetworkName(tx.network, language)}
              </td>
              <td className="token-supply">
                {tx.initial_supply.toLocaleString()}
              </td>
              <td className="token-date">
                {new Date(tx.created_at).toLocaleDateString()}
              </td>
              <td className="token-metadata">
                <button 
                  className="metadata-register-btn"
                  onClick={() => handleRegisterClick({
                    tokenAddress: tx.token_address,
                    tokenName: tx.token_name,
                    tokenSymbol: tx.token_symbol,
                    network: tx.network
                  })}
                >
                  {tx.metadata_name 
                    ? (language === 'KR' ? '수정' : 'Edit') 
                    : (language === 'KR' ? '등록' : 'Register')}
                </button>
              </td>
              <td className="token-actions">
                <button 
                  className="token-delete-btn"
                  onClick={() => handleDeleteClick({
                    tokenAddress: tx.token_address,
                    tokenName: tx.token_name,
                    tokenSymbol: tx.token_symbol,
                    userId: tx.user_id
                  })}
                >
                  {language === 'KR' ? '삭제' : 'Delete'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 메타데이터 등록 모달 창 */}
      {showRegisterWindow && selectedToken && (
        <div className="metadata-modal-overlay" onClick={handleCloseRegister}>
          <div className="metadata-modal-container" onClick={e => e.stopPropagation()}>
            <RegisterMetadata
              tokenAddress={selectedToken.tokenAddress}
              tokenName={selectedToken.tokenName}
              tokenSymbol={selectedToken.tokenSymbol}
              network={selectedToken.network || network}
              language={language}
              onClose={handleCloseRegister}
              onSuccess={handleRegisterSuccess}
            />
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && tokenToDelete && (
        <div className="delete-confirm-overlay" onClick={handleCancelDelete}>
          <div className="delete-confirm-container" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <h3>
                {language === 'KR' 
                  ? '토큰 기록 삭제 확인' 
                  : 'Confirm Token Record Deletion'}
              </h3>
              <button className="close-button" onClick={handleCancelDelete}>×</button>
            </div>
            
            <div className="delete-confirm-content">
              <div className="warning-icon">⚠️</div>
              <p className="delete-warning">
                {language === 'KR' 
                  ? '주의: 이 작업은 데이터베이스에서만 토큰 기록을 삭제합니다.' 
                  : 'Warning: This action only removes the token record from the database.'}
              </p>
              <p className="delete-warning-subtext">
                {language === 'KR' 
                  ? '블록체인에서 토큰 자체를 삭제할 수는 없습니다. 토큰은 블록체인에 계속 존재하지만 이 인터페이스에서는 더 이상 표시되지 않습니다.' 
                  : 'The token itself cannot be deleted from the blockchain. It will continue to exist on the blockchain but will no longer be displayed in this interface.'}
              </p>
              
              <div className="token-info">
                <p>
                  {language === 'KR' ? '토큰 이름' : 'Token Name'}: 
                  <span>{tokenToDelete.tokenName} ({tokenToDelete.tokenSymbol})</span>
                </p>
                <p>
                  {language === 'KR' ? '토큰 주소' : 'Token Address'}: 
                  <span>{truncateAddress(tokenToDelete.tokenAddress, 8, 8)}</span>
                </p>
              </div>
              
              <p className="delete-question">
                {language === 'KR' 
                  ? '정말로 이 토큰 기록을 삭제하시겠습니까?' 
                  : 'Are you sure you want to delete this token record?'}
              </p>
              
              {deleteError && (
                <div className="delete-error-message">
                  {deleteError}
                </div>
              )}
              
              <div className="delete-confirm-actions">
                <button 
                  className="delete-confirm-btn"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? (language === 'KR' ? '삭제 중...' : 'Deleting...')
                    : (language === 'KR' ? '삭제 확인' : 'Confirm Delete')}
                </button>
                <button 
                  className="delete-cancel-btn"
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                >
                  {language === 'KR' ? '취소' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListTokens;